import React from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import type { Block, Inline, LegalDoc } from "./legalMarkdown";
import "./LegalDocument.css";

// Distance from the top of the scroll viewport at which a section counts as the
// one being read (sits just under the topbar + progress bar).
const ACTIVE_OFFSET = 96;
const SCROLL_TO_OFFSET = 24;
const SHOW_TOP_AFTER = 460;

export interface LegalDocumentProps {
  doc: LegalDoc;
  /** Short name shown in the topbar. */
  shortTitle: string;
  /** Icon that badges the document kind in the hero eyebrow. */
  icon: React.ReactNode;
  testId: string;
  versionNotice?: React.ReactNode;
}

type NavigateFn = (path: string) => void;

function renderInline(nodes: Inline[], onNavigate: NavigateFn, keyBase: string): React.ReactNode {
  return nodes.map((node, idx) => {
    const key = `${keyBase}-${idx}`;
    switch (node.t) {
      case "text":
        return <React.Fragment key={key}>{node.value}</React.Fragment>;
      case "strong":
        return <strong key={key}>{renderInline(node.children, onNavigate, key)}</strong>;
      case "code":
        return (
          <code key={key} className="legalDoc-code">
            {node.value}
          </code>
        );
      case "link": {
        const label = renderInline(node.children, onNavigate, key);
        if (node.internal) {
          return (
            <a
              key={key}
              className="legalDoc-link"
              href={node.href}
              onClick={(e) => {
                e.preventDefault();
                onNavigate(node.href);
              }}
            >
              {label}
            </a>
          );
        }
        const external = node.href.startsWith("http");
        return (
          <a
            key={key}
            className="legalDoc-link"
            href={node.href}
            {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
          >
            {label}
          </a>
        );
      }
      default:
        return null;
    }
  });
}

function renderBlocks(blocks: Block[], onNavigate: NavigateFn, keyBase: string): React.ReactNode {
  return blocks.map((block, idx) => {
    const key = `${keyBase}-${idx}`;
    switch (block.t) {
      case "p":
        return (
          <p key={key} className="legalDoc-p">
            {renderInline(block.content, onNavigate, key)}
          </p>
        );
      case "ul":
        return (
          <ul key={key} className="legalDoc-list">
            {block.items.map((item, j) => (
              <li key={`${key}-${j}`}>{renderInline(item, onNavigate, `${key}-${j}`)}</li>
            ))}
          </ul>
        );
      case "ol":
        return (
          <ol key={key} className="legalDoc-list legalDoc-list--ordered">
            {block.items.map((item, j) => (
              <li key={`${key}-${j}`}>{renderInline(item, onNavigate, `${key}-${j}`)}</li>
            ))}
          </ol>
        );
      case "table":
        return (
          <div
            key={key}
            className="legalDoc-tableWrap"
            role="region"
            aria-label="Tabela"
            tabIndex={0}
          >
            <table className="legalDoc-table">
              <thead>
                <tr>
                  {block.head.map((cell, j) => (
                    <th key={`${key}-h-${j}`}>{renderInline(cell, onNavigate, `${key}-h-${j}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, r) => (
                  <tr key={`${key}-r-${r}`}>
                    {row.map((cell, c) => (
                      <td key={`${key}-r-${r}-${c}`}>
                        {renderInline(cell, onNavigate, `${key}-r-${r}-${c}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case "note":
        return (
          <aside key={key} className="legalDoc-note">
            <InfoOutlinedIcon className="legalDoc-note-icon" />
            <div className="legalDoc-note-body">{renderBlocks(block.blocks, onNavigate, key)}</div>
          </aside>
        );
      case "hr":
        return <hr key={key} className="legalDoc-hr" />;
      default:
        return null;
    }
  });
}

export const LegalDocument: React.FC<LegalDocumentProps> = ({
  doc,
  shortTitle,
  icon,
  testId,
  versionNotice,
}) => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 767px)");

  const scrollElRef = React.useRef<HTMLDivElement | null>(null);
  const sectionRefs = React.useRef<Map<string, HTMLElement>>(new Map());
  const tickingRef = React.useRef(false);

  const [activeId, setActiveId] = React.useState<string | null>(doc.sections[0]?.id ?? null);
  const [progress, setProgress] = React.useState(0);
  const [showTop, setShowTop] = React.useState(false);
  const [tocOpen, setTocOpen] = React.useState(false);

  const onNavigate = React.useCallback<NavigateFn>((path) => navigate(path), [navigate]);

  const recompute = React.useCallback(() => {
    const scrollEl = scrollElRef.current;
    if (!scrollEl) return;
    const scrollTop = scrollEl.scrollTop;
    const max = scrollEl.scrollHeight - scrollEl.clientHeight;
    setProgress(max > 0 ? Math.min(100, Math.max(0, (scrollTop / max) * 100)) : 0);
    setShowTop(scrollTop > SHOW_TOP_AFTER);

    const containerTop = scrollEl.getBoundingClientRect().top;
    let current = doc.sections[0]?.id ?? null;
    for (const section of doc.sections) {
      const el = sectionRefs.current.get(section.id);
      if (!el) continue;
      const top = el.getBoundingClientRect().top - containerTop;
      if (top <= ACTIVE_OFFSET) current = section.id;
      else break;
    }
    setActiveId(current);
  }, [doc.sections]);

  const handleScroll = React.useCallback(() => {
    if (tickingRef.current) return;
    tickingRef.current = true;
    requestAnimationFrame(() => {
      tickingRef.current = false;
      recompute();
    });
  }, [recompute]);

  React.useEffect(() => {
    recompute();
  }, [recompute]);

  // Close the mobile index sheet as soon as the layout grows to desktop.
  React.useEffect(() => {
    if (isDesktop) setTocOpen(false);
  }, [isDesktop]);

  const registerSection = React.useCallback(
    (id: string) => (el: HTMLElement | null) => {
      if (el) sectionRefs.current.set(id, el);
      else sectionRefs.current.delete(id);
    },
    [],
  );

  const goToSection = React.useCallback((id: string) => {
    const el = sectionRefs.current.get(id);
    const scrollEl = scrollElRef.current;
    if (!el || !scrollEl) return;
    const top =
      el.getBoundingClientRect().top -
      scrollEl.getBoundingClientRect().top +
      scrollEl.scrollTop -
      SCROLL_TO_OFFSET;
    scrollEl.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    setTocOpen(false);
  }, []);

  const goTop = React.useCallback(() => {
    scrollElRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const tocNav = (variant: "rail" | "sheet") => (
    <nav className="legalDoc-tocNav" aria-label="Índice do documento">
      {doc.sections.map((section) => (
        <button
          key={section.id}
          type="button"
          className={`legalDoc-tocItem${activeId === section.id ? " is-active" : ""}`}
          onClick={() => goToSection(section.id)}
        >
          {section.number && <span className="legalDoc-tocNum">{section.number}</span>}
          <span className="legalDoc-tocLabel">{section.title}</span>
        </button>
      ))}
      {variant === "sheet" && <div className="legalDoc-sheetSpacer" aria-hidden="true" />}
    </nav>
  );

  return (
    <div className="legalPage" data-testid={testId}>
      <header className="legalDoc-topbar">
        <button
          type="button"
          className="legalDoc-topbarBtn"
          onClick={() => navigate(-1)}
          aria-label="Voltar"
        >
          <ArrowBackIosNewRoundedIcon />
        </button>
        <span className="legalDoc-topbarTitle">{shortTitle}</span>
        {isDesktop && (
          <button
            type="button"
            className="legalDoc-topbarBtn"
            onClick={() => window.print()}
            aria-label="Imprimir ou salvar em PDF"
          >
            <PrintOutlinedIcon />
          </button>
        )}
      </header>

      <div className="legalDoc-scroll" ref={scrollElRef} onScroll={handleScroll}>
        <div className="legalDoc-progress" aria-hidden="true">
          <div className="legalDoc-progress-bar" style={{ width: `${progress}%` }} />
        </div>

        <div className="legalDoc">
          <header className="legalDoc-hero">
            <div className="legalDoc-eyebrow">
              <span className="legalDoc-eyebrowIcon">{icon}</span>
              <span>DaKasa · Documento oficial</span>
            </div>
            <h1 className="legalDoc-title">{doc.title}</h1>
            <div className="legalDoc-chips">
              {doc.vigencia && (
                <span className="legalDoc-chip">
                  <EventOutlinedIcon />
                  Vigência {doc.vigencia}
                </span>
              )}
              {doc.versao && (
                <span className="legalDoc-chip">
                  <SellOutlinedIcon />
                  Versão {doc.versao}
                </span>
              )}
            </div>
          </header>

          <div className="legalDoc-body">
            {isDesktop && (
              <aside className="legalDoc-toc" aria-label="Índice">
                <div className="legalDoc-toc-inner">
                  <div className="legalDoc-toc-head">
                    <FormatListBulletedRoundedIcon />
                    <span>Índice</span>
                  </div>
                  <div className="legalDoc-toc-rail" aria-hidden="true">
                    <div className="legalDoc-toc-railFill" style={{ height: `${progress}%` }} />
                  </div>
                  {tocNav("rail")}
                </div>
              </aside>
            )}

            <article className="legalDoc-article">
              {versionNotice && (
                <p className="legalDoc-p" role="status">
                  {versionNotice}
                </p>
              )}
              {doc.intro.length > 0 && (
                <div className="legalDoc-intro">{renderBlocks(doc.intro, onNavigate, "intro")}</div>
              )}

              {doc.sections.map((section) => (
                <section
                  key={section.id}
                  ref={registerSection(section.id)}
                  className="legalDoc-section"
                  aria-labelledby={`${section.id}-h`}
                >
                  <h2 id={`${section.id}-h`} className="legalDoc-h2">
                    {section.number && <span className="legalDoc-h2-num">{section.number}</span>}
                    <span className="legalDoc-h2-text">{section.title}</span>
                  </h2>
                  {renderBlocks(section.blocks, onNavigate, section.id)}
                </section>
              ))}

              <footer className="legalDoc-foot">
                <InfoOutlinedIcon />
                <span>
                  DaKasa · Dakasa Social Mídia &amp; Engajamentos Ltda · CNPJ 53.411.564/0001-83
                </span>
              </footer>
            </article>
          </div>
        </div>
      </div>

      <div className="legalDoc-floating" data-desktop={isDesktop ? "true" : "false"}>
        {isMobile && (
          <button
            type="button"
            className="legalDoc-fab legalDoc-fab--toc"
            onClick={() => setTocOpen(true)}
          >
            <FormatListBulletedRoundedIcon />
            <span>Índice</span>
          </button>
        )}
        <button
          type="button"
          className={`legalDoc-fab legalDoc-fab--top${showTop ? " is-shown" : ""}`}
          onClick={goTop}
          aria-label="Voltar ao topo"
        >
          <ArrowUpwardRoundedIcon />
        </button>
      </div>

      {isMobile && tocOpen && (
        <div className="legalDoc-sheet" role="dialog" aria-modal="true" aria-label="Índice">
          <div className="legalDoc-sheet-scrim" onClick={() => setTocOpen(false)} />
          <div className="legalDoc-sheet-panel">
            <div className="legalDoc-sheet-head">
              <span className="legalDoc-sheet-title">
                <FormatListBulletedRoundedIcon />
                Índice
              </span>
              <button
                type="button"
                className="legalDoc-sheet-close"
                onClick={() => setTocOpen(false)}
                aria-label="Fechar índice"
              >
                <CloseRoundedIcon />
              </button>
            </div>
            <div className="legalDoc-sheet-body">{tocNav("sheet")}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalDocument;
